/**
 * Story 5.2 — Cart line-item edit bridge (Horizon ThemeEvents.cartUpdate →
 * benvel:cart-updated + persistent aria-live announcer update).
 *
 * Side-effect ES module. Strictly additive — does NOT modify Horizon JS.
 *
 * Responsibilities:
 *   1. Listen on `document` for Horizon-native `cart:update` events whose
 *      `detail.sourceId === 'cart-items-component'` (the line-edit / remove
 *      origin from `component-cart-items.js`). Ignore ATC-originated events
 *      (Story 5.1's `cart-drawer.js` handles those).
 *   2. Update every `[data-cart-line-edit-announcer]` element's `textContent`
 *      with the appropriate Spanish announcement (AC4, WCAG 2.2 SC 4.1.3).
 *   3. Dispatch `benvel:cart-updated` with `source: 'line-edit' | 'remove'`
 *      derived from item_count delta (AC8; docs/events.md).
 *
 * No new Web Component (project-context §22). No `events.dispatch` helper
 * (Horizon's events.js exports no such helper — events.md mandates raw
 * `CustomEvent` for `benvel:*`).
 */

(() => {
  // Idempotency guard. Multiple includes (drawer + cart-page templates) must
  // wire one listener only (project-context §22).
  if (window.__benvelCartLineEditMounted) return;
  window.__benvelCartLineEditMounted = true;

  // Horizon's ThemeEvents.cartUpdate constant resolves to 'cart:update'
  // (assets/events.js line 14). Hard-coded here so the bridge has no
  // import dependency on Horizon's class-based event registry (events.md).
  const CART_UPDATE_EVENT = 'cart:update';

  // The Horizon source-id the bridge listens for. Other sources (e.g. the
  // drawer's add-to-cart fire) are skipped — those flow through 5.1.
  const LINE_EDIT_SOURCE_ID = 'cart-items-component';

  // Cached number formatter. Hard-codes es-CO + COP per binding decision in
  // Dev Notes (Phase D multi-locale follow-up; ews-bu-benvel store is CO-only
  // in v1). TODO Phase D: read from window.Shopify.locale + currency.
  const totalFormatter = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  });

  // State for source-derivation heuristic. Compares incoming item_count to
  // last-known. `null` until first event (see #initialItemCount).
  let lastKnownItemCount = null;
  // Pre-event cache of line titles by (line_index → title), seeded from the
  // DOM and refreshed on every event. Lets the remove announcement carry the
  // product title even after the row is detached.
  const lastKnownLineTitles = new Map();

  /**
   * Seeds lastKnownItemCount + lastKnownLineTitles from the current DOM
   * before any event fires. Called on DOMContentLoaded (or immediately if
   * the doc is already interactive). Best-effort: missing attributes are
   * tolerated.
   */
  function seedFromDom() {
    // Read item_count from cart-icon's badge attribute, or fall back to
    // counting [ref^="cartItemRows"] rows.
    const bubble = document.querySelector('[data-cart-count]');
    if (bubble && bubble.dataset.cartCount != null) {
      const parsed = parseInt(bubble.dataset.cartCount, 10);
      if (Number.isFinite(parsed)) lastKnownItemCount = parsed;
    }
    if (lastKnownItemCount == null) {
      const rows = document.querySelectorAll('[ref^="cartItemRows"] tr.cart-items__row, .cart-items__table-row');
      if (rows.length > 0) lastKnownItemCount = rows.length;
    }
  }

  /**
   * Reads the locale string by dotted key from `window.Theme.translations`
   * (Horizon-default injected dict). Falls back to a hard-coded Spanish
   * string if the dict is missing or the key absent.
   *
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  function t(key, fallback) {
    const dict = /** @type {Record<string, string> | undefined} */ (
      window.Theme && window.Theme.translations
    );
    if (dict && typeof dict[key] === 'string' && dict[key].length > 0) {
      return dict[key];
    }
    return fallback;
  }

  /**
   * Substitutes `{{ name }}` placeholders in a locale template.
   * @param {string} template
   * @param {Record<string, string | number>} subs
   * @returns {string}
   */
  function interpolate(template, subs) {
    let out = template;
    for (const [name, value] of Object.entries(subs)) {
      out = out.split(`{{ ${name} }}`).join(String(value));
    }
    return out;
  }

  /**
   * Updates every announcer region's textContent. Touching textContent on
   * an aria-live="polite" region triggers an SR announcement.
   *
   * @param {string} message
   */
  function updateAnnouncers(message) {
    const announcers = document.querySelectorAll('[data-cart-line-edit-announcer]');
    announcers.forEach((el) => {
      el.textContent = message;
    });
  }

  /**
   * Dispatches the benvel:cart-updated CustomEvent on `document`. Schema is
   * canonicalized in docs/events.md.
   *
   * @param {object | null} cart
   * @param {'line-edit' | 'remove'} source
   * @param {number | null} cartItemCount
   * @param {number | null} cartTotal
   */
  function dispatchCartUpdated(cart, source, cartItemCount, cartTotal) {
    document.dispatchEvent(
      new CustomEvent('benvel:cart-updated', {
        bubbles: true,
        detail: {
          cart,
          source,
          cartItemCount,
          cartTotal,
        },
      })
    );
  }

  /**
   * Caches line titles from the current DOM. Called BEFORE the morph so we
   * can recover the removed product's title for the announcement.
   */
  function refreshTitleCache() {
    lastKnownLineTitles.clear();
    document.querySelectorAll('.cart-items__title').forEach((el) => {
      const title = el.textContent?.trim();
      if (!title) return;
      // Walk up to the row to find its line-index (Horizon uses 1-indexed).
      const row = el.closest('[ref^="cartItemRows"]') || el.closest('.cart-items__table-row');
      const refAttr = row?.getAttribute('ref') || '';
      const match = refAttr.match(/cartItemRows-(\d+)/);
      const key = match ? match[1] : title;
      lastKnownLineTitles.set(key, title);
    });
  }

  /**
   * Picks a representative removed-line title from the cache by diffing
   * against the post-event payload's items. Returns the first cached title
   * not present in the new cart's items list, or empty string if uncertain.
   *
   * @param {Array<{ title?: string }> | undefined} newItems
   * @returns {string}
   */
  function pickRemovedTitle(newItems) {
    if (!Array.isArray(newItems)) {
      // Best-effort: return any cached title. Acceptable per AC8.
      const first = lastKnownLineTitles.values().next().value;
      return typeof first === 'string' ? first : '';
    }
    const remaining = new Set(newItems.map((item) => (item.title || '').trim()).filter(Boolean));
    for (const cached of lastKnownLineTitles.values()) {
      if (!remaining.has(cached)) return cached;
    }
    const first = lastKnownLineTitles.values().next().value;
    return typeof first === 'string' ? first : '';
  }

  /**
   * Main event handler. Filters for line-edit source, derives source label,
   * computes announcement, updates DOM, dispatches benvel event.
   *
   * @param {Event} event - The Horizon CartUpdateEvent (CustomEvent shape).
   */
  function handleCartUpdate(event) {
    // CartUpdateEvent shape: detail = { resource, sourceId, data: { source, itemCount, sections, ... } }.
    // assets/events.js CartUpdateEvent (line 122) and CartAddEvent (line 91)
    // both fire under 'cart:update'; only CartUpdateEvent carries the
    // post-/cart/change.js data we need. Filter by detail.data.source (the
    // emitting component ID), NOT detail.sourceId (= section-id like 'cart-drawer').
    const detail = /** @type {{ sourceId?: string, resource?: object, data?: { itemCount?: number, source?: string, sections?: Record<string, string> } }} */ (
      event && event.detail
    );
    if (!detail || detail.data?.source !== LINE_EDIT_SOURCE_ID) return;

    const data = detail.data || {};
    const newItemCount = typeof data.itemCount === 'number' ? data.itemCount : null;

    // Source derivation: 'remove' if count decreased; 'line-edit' otherwise.
    // First event after page load (lastKnownItemCount === null) defaults to
    // 'line-edit' per Dev Notes "Source-derivation heuristic".
    let source = 'line-edit';
    if (lastKnownItemCount !== null && newItemCount !== null && newItemCount < lastKnownItemCount) {
      source = 'remove';
    }

    // The Horizon CartUpdateEvent does NOT carry the full /cart.js shape in
    // `detail.data` — only `itemCount` + `source` + `sections`. Fetch
    // /cart.js to populate the `cart` field. Non-blocking: the announcer
    // update runs synchronously; the benvel event waits on cart fetch.
    const cartFetch = fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    // Compute announcement immediately (do not wait on fetch — the
    // /cart/change.js POST has already resolved by the time we're here, so
    // the morphed DOM has the new totals; aria-live should fire ASAP).
    cartFetch.then((cart) => {
      const totalPrice = typeof cart?.total_price === 'number' ? cart.total_price : 0;
      const formattedTotal = totalFormatter.format(totalPrice / 100);

      let template;
      let subs;
      if (source === 'remove') {
        const removedTitle = pickRemovedTitle(cart?.items);
        template = t(
          'cart.line_edit.item_removed_announcement',
          '{{ product_title }} eliminado de tu carrito. Total: {{ formatted_total }}'
        );
        subs = {
          product_title: removedTitle || 'Producto',
          formatted_total: formattedTotal,
        };
      } else {
        template = t(
          'cart.line_edit.total_updated_announcement',
          'Total actualizado: {{ formatted_total }}, {{ item_count }} artículos en tu carrito'
        );
        subs = {
          formatted_total: formattedTotal,
          item_count: newItemCount ?? 0,
        };
      }
      const announcement = interpolate(template, subs);
      updateAnnouncers(announcement);

      // Dispatch the benvel event after the announcer fires so any synchronous
      // listener that inspects DOM sees the updated announcer.
      dispatchCartUpdated(cart, source, newItemCount, totalPrice);
    });

    // Update tracking state immediately so back-to-back events derive source
    // from the freshest known count (do not wait on fetch).
    if (newItemCount !== null) lastKnownItemCount = newItemCount;
    refreshTitleCache();
  }

  // Wire listener. Capture-phase NOT needed — the bridge is downstream of
  // Horizon's #handleCartUpdate (component-cart-items.js).
  document.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);

  // Seed initial state.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      seedFromDom();
      refreshTitleCache();
    });
  } else {
    seedFromDom();
    refreshTitleCache();
  }
})();
